import { PRODUCT_STATUSES, XERO_STATUS_NOT_IN_XERO } from '@shared/types/product-status';
import { SHOPIFY_STATUSES } from '@shared/types/shopify-status';
import { splitBrandRefs } from '@shared/brand-filter';
import type { DecodedSort } from '@shared/report-view-state';
import type { ProductsGroupBy, ProductsQueryParams } from '@/lib/products-api';
import { TABLE_PAGING_URL } from '@/lib/table-paging';
import {
  urlBool,
  urlColsMask,
  urlEnum,
  urlIdList,
  urlInt,
  urlList,
  urlSort,
  urlText,
} from '@/lib/url-state/codecs';
import { urlHref, type UrlValues } from '@/lib/url-state/url-schema';

export const PRODUCTS_GROUP_BY = ['variant', 'product'] as const satisfies readonly ProductsGroupBy[];

/** Stock level filter bounds: 0 to this is no filter. */
export const PRODUCTS_STOCK_MAX = 99999;

/** The Xero status filter's choices ('all' shows every status, as the API reads it). */
export const PRODUCTS_XERO_STATUSES = [
  'active',
  'inactive',
  'archived',
  XERO_STATUS_NOT_IN_XERO,
  'all',
] as const;

/** The table's columns by variant and by product (their DataTable ids), in order. */
export const PRODUCTS_VARIANT_COLUMN_IDS = [
  'vendor_name',
  'product_title',
  'product_type',
  'code',
  'displayname',
  'unitprice',
  'quantityonhand',
  'status',
  'xero_status',
] as const;

export const PRODUCTS_PRODUCT_COLUMN_IDS = [
  'vendor_name',
  'product_title',
  'product_type',
  'variant_count',
  'unitprice',
  'quantityonhand',
  'status',
  'xero_status',
] as const;

/** Columns the list sorts by (their DataTable ids); each group sorts by its own columns. */
export const PRODUCTS_SORT_COLUMNS = [
  ...new Set([...PRODUCTS_VARIANT_COLUMN_IDS, ...PRODUCTS_PRODUCT_COLUMN_IDS]),
] as const;

/** No sort in the URL: the group's own default (below). */
const NO_SORT: DecodedSort = { id: '', dir: 'asc' };

/**
 * /products in the URL. Until T-196 the keys were `query`, `vendors` (brand names), `suppliers`,
 * `status`, `xeroStatus` and `sortField`/`sortDirection`; they're still read, so bookmarks and old
 * links open the same list (decision 30 c). `vendorIds` holds brand ids; a name from an old link
 * stays a name until the brands have loaded, then the page writes its id (`resolveBrandRefs`).
 * `cols` comes only from a link or a saved view: column choices are saved in storage.
 */
export const PRODUCTS_URL = {
  groupBy: urlEnum('groupBy', PRODUCTS_GROUP_BY, 'variant'),
  q: urlText('q', { aliases: ['query'] }),
  vendorIds: urlIdList('vendorIds', { aliases: ['vendors'] }),
  supplierIds: urlIdList('supplierIds', { aliases: ['suppliers'] }),
  productTypes: urlList('productTypes'),
  productStatuses: urlList('productStatuses', { aliases: ['status'], values: PRODUCT_STATUSES }),
  shopifyStatuses: urlList('shopifyStatuses', { values: SHOPIFY_STATUSES }),
  xeroStatuses: urlList('xeroStatuses', { aliases: ['xeroStatus'], values: PRODUCTS_XERO_STATUSES }),
  minStock: urlInt('minStock', { min: 0, max: PRODUCTS_STOCK_MAX, default: 0 }),
  maxStock: urlInt('maxStock', { min: 0, max: PRODUCTS_STOCK_MAX, default: PRODUCTS_STOCK_MAX }),
  /** "Status overridden in jflo": jflo's status differs from what the sync would set (T-236). */
  statusOverridden: urlBool('statusOverridden'),
  sort: urlSort({
    columns: PRODUCTS_SORT_COLUMNS,
    default: NO_SORT,
    legacy: { field: 'sortField', direction: 'sortDirection' },
  }),
  cols: urlColsMask(),
  ...TABLE_PAGING_URL,
};

export type ProductsView = UrlValues<typeof PRODUCTS_URL>;

/** Remembered per team: the sidebar and a record page's breadcrumb bring back the last view. */
export const PRODUCTS_VIEW_ID = 'products';

/** A link to /products with these filters (the rest default). */
export function productsHref(view: Partial<ProductsView>): string {
  return urlHref('/products', PRODUCTS_URL, view);
}

export function defaultProductsSort(groupBy: ProductsGroupBy): DecodedSort {
  return { id: groupBy === 'product' ? 'product_title' : 'displayname', dir: 'asc' };
}

/** The sort to show: the URL's when the group has that column, otherwise the group's default. */
export function productsSort(view: Pick<ProductsView, 'sort' | 'groupBy'>): DecodedSort {
  const columns: readonly string[] =
    view.groupBy === 'product' ? PRODUCTS_PRODUCT_COLUMN_IDS : PRODUCTS_VARIANT_COLUMN_IDS;
  return columns.includes(view.sort.id) ? view.sort : defaultProductsSort(view.groupBy);
}

/** The sort to write: none when it's the group's default, so the URL stays short. */
export function productsSortForUrl(sort: DecodedSort, groupBy: ProductsGroupBy): DecodedSort {
  const fallback = defaultProductsSort(groupBy);
  return sort.id === fallback.id && sort.dir === fallback.dir ? NO_SORT : sort;
}

/** The stock range, its bounds in order. */
export function productsStockRange(view: Pick<ProductsView, 'minStock' | 'maxStock'>) {
  return { min: view.minStock, max: Math.max(view.minStock, view.maxStock) };
}

/** The list request for a view (every page; the caller adds `page` and `limit`). */
export function productsListQuery(
  view: ProductsView,
  stock: { min: number; max: number } = productsStockRange(view)
): ProductsQueryParams {
  const brands = splitBrandRefs(view.vendorIds);
  const sort = productsSort(view);
  const list = <T>(values: T[]) => (values.length > 0 ? values : undefined);
  return {
    query: view.q || undefined,
    vendorIds: list(brands.ids),
    vendors: list(brands.names),
    suppliers: list(view.supplierIds),
    productTypes: list(view.productTypes),
    status: list(view.productStatuses),
    shopifyStatuses: list(view.shopifyStatuses),
    xeroStatus: list(view.xeroStatuses),
    minStock: stock.min,
    maxStock: stock.max,
    sortField: sort.id,
    sortDirection: sort.dir === 'desc' ? 'descending' : 'ascending',
    groupBy: view.groupBy,
    statusOverridden: view.statusOverridden || undefined,
  };
}
